import { useEffect, useState } from 'react';
import { InventoryAsset, SupportCasesItem } from '../../../types';
import {
  getNewCaseCreatedSubject,
  getNewCaseProcessingSubject,
  getCaseLoadingSubject,
  getCasesByAssetId,
  getCaseSubject,
  newCaseCreatedSubject,
  useSupportCases
} from '../../../store/SupportCase';
import { CaseLink } from '../../Compounds/UpstackDataGrid/CellRenderers';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { DetailViewCard } from '../../Compounds/CardWithTitle';
import { PermissibleRender } from '../../Compounds/PermissibleRender';
import { SupportCaseWizard } from '../../Compounds/Wizards/SupportCaseWizard';
import { Permission } from '../../../store/GeneralStore';

export interface CaseDetailsDisplayProps {
  data: InventoryAsset;
  onCasesChanged?: () => Promise<void>;
  disabled?: boolean;
  disabledMessage?: string;
}

export function CaseDetailsDisplay({
  data,
  onCasesChanged,
  disabled = false,
  disabledMessage = ''
}: CaseDetailsDisplayProps) {
  const [casesLoading, setCasesLoading] = useState<boolean>(false);
  const [casesProcessing, setCasesProcessing] = useState<boolean>(false);
  const [newCaseCreated, setNewCaseCreated] = useState<boolean>(false);

  const [casesCollapsed, setCasesCollapsed] = useState<boolean>(true);
  const [cases, setCases] = useState<SupportCasesItem[]>([]);
  const { fetchCases } = useSupportCases();

  const orderCasesByDate = (cases: SupportCasesItem[]) => {
    return cases.sort((a, b) => {
      const dateA = new Date(a.dateTimeOpened);
      const dateB = new Date(b.dateTimeOpened);
      return dateB.getTime() - dateA.getTime();
    });
  };

  useEffect(() => {
    const caseLoadingSub = getCaseLoadingSubject().subscribe((state) => setCasesLoading(state));
    const caseSub = getCaseSubject().subscribe(() => setCases(orderCasesByDate(getCasesByAssetId(data.id))));
    const caseProcessingSub = getNewCaseProcessingSubject().subscribe((p) => setCasesProcessing(p));
    const newCaseSub = getNewCaseCreatedSubject().subscribe((n) => setNewCaseCreated(n));

    fetchCases();

    return () => {
      if (caseLoadingSub) caseLoadingSub.unsubscribe();
      if (caseSub) caseSub.unsubscribe();
      if (caseProcessingSub) caseProcessingSub.unsubscribe();
      if (newCaseSub) newCaseSub.unsubscribe();
      setCasesCollapsed(true);
      newCaseCreatedSubject.next(false);
    };
  }, []);

  useEffect(() => {
    setCases(orderCasesByDate(getCasesByAssetId(data.id)));
  }, [data?.id]);

  useEffect(() => {
    if (newCaseCreated) setCasesCollapsed(false);
  }, [newCaseCreated]);

  const cardActions = () => (
    <PermissibleRender requiredPermissions={[Permission.CREATE_CASES_FROM_INVENTORY]}>
      <SupportCaseWizard
        ctaClassName="text-grey-12 text-sm"
        inventoryAsset={data}
        onFormCloseHandler={async () => casesChanged()}
        disabled={disabled}
        disabledMessage={disabledMessage}
      />
    </PermissibleRender>
  );

  const casesChanged = async () => {
    fetchCases(true);
    if (onCasesChanged) {
      await onCasesChanged();
    }
  };

  return (
    <DetailViewCard
      cardActions={cardActions()}
      collapsed={casesCollapsed}
      enableCollapse
      key="inventory-cases"
      title={`Support Cases (${cases?.length || 0})`}>
      <div className="max-h-72">
        {(casesLoading || casesProcessing) && (
          <div className="mt-28">
            <ComponentSpinner />
          </div>
        )}
        {!disabled && !casesLoading && !cases?.length && <div className="text-center">No Cases</div>}
        {disabled && <div className="text-center">{disabledMessage}</div>}
        {!casesLoading && !!cases?.length && (
          <div className="h-60 overflow-auto">
            <table
              data-testid="table"
              className="w-full [&_tr]:border-y [&_tr]:h-[30px]">
              <thead>
                <tr className="text-indigo-mid text-xs text-left [&>*]:font-normal truncate [&>*]:p-2">
                  <th className="border-r"></th>
                  <th className="border-r">Status</th>
                  <th className="border-r">Case Number</th>
                  <th className="border-r">Issue</th>
                  <th className="border-r">Date/Time Opened</th>
                </tr>
              </thead>
              <tbody className="h-2/3 overflow-scroll">
                {cases?.map((case_) => (
                  <tr
                    key={`${case_.caseNumber}-${case_.id}`}
                    className="text-black text-sm text-left font-normal truncate [&>*]:p-[0.65rem]">
                    <td className="pt-[-20px] border-r min-w-[38px]">
                      <CaseLink
                        data={case_}
                        classNames="h-[100%]"
                      />
                    </td>
                    <td className="border-r min-w-[90px] text-ellipsis">{case_.status}</td>
                    <td className="border-r min-w-[100px]">{case_.caseNumber}</td>
                    <td className="border-r min-w-[100px]">{case_.issue}</td>
                    <td className="border-r min-w-[100px]">{case_.dateTimeOpened}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DetailViewCard>
  );
}
